import { Box, Stack } from "@mui/system";
import * as React from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Sidebar from "./Sidebar";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import { styled } from "@mui/material/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CallMissedOutgoingIcon from "@mui/icons-material/CallMissedOutgoing";
import Modal from "@mui/material/Modal";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { toast, ToastContainer } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddchartIcon from "@mui/icons-material/Addchart";
import { AuthContext } from "../../AuthContext.js";
import { ContactPhone, EditAttributes, PhoneCallback } from "@mui/icons-material";
const { DateTime } = require("luxon");

const carBrandNames = require("../AdditionalPages/VehicleBrands.js");

// --- MODERN STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "92%",
  maxWidth: "1020px",
  maxHeight: "92vh",
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "24px",
  borderTop: "5px solid #2563EB",
  boxShadow: "0 25px 60px -15px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(226, 232, 240, 0.8)",
  padding: "32px 36px",
  outline: "none",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#F1F5F9",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: "8px",
    "&:hover": { background: "#94A3B8" },
  },
  [theme.breakpoints.down("md")]: {
    width: "95%",
    padding: "24px 18px",
  },
}));

const CustomerModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  padding: "24px",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "90%",
  },
}));

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
  "& .highlighted-row": {
    backgroundColor: "#F0FDFA",
    "&:hover": {
      backgroundColor: "#CCFBF1",
    },
  },
  "& .wrap-text": {
    whiteSpace: "normal",
  },
});

const SearchContainer = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  "&:hover": { borderColor: "#CBD5E1" },
  width: "320px",
  display: "flex",
  alignItems: "center",
  padding: "4px 12px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const StatChip = styled(Box)(({ color }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: "12px",
  backgroundColor: `${color}15`,
  color: color,
  fontWeight: 600,
  fontSize: "0.9rem",
}));

const StyledDatePicker = styled(DatePicker)({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
  },
});

const SectionTitle = styled(Typography)({
  color: "#3B82F6",
  fontWeight: 800,
  fontSize: "0.875rem",
  marginBottom: "16px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

function Leads() {
  return (
    <RootContainer>
      <Sidebar />
      <LeadComponent />
    </RootContainer>
  );
}

const LeadComponent = () => {
  const columns = [
    {
      field: "col0",
      headerName: "Actions",
      width: 220,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              sx={{
                bgcolor: "#3B82F615",
                color: "#3B82F6",
                "&:hover": { bgcolor: "#3B82F6", color: "white" },
              }}
              onClick={(event) => handleEdit(event, cellValues)}
            >
              <EditIcon fontSize="small" />
            </IconButton>

            {localStorage.getItem("roleId") == "af7c1fe6-d669-414e-b066-e9733f0de7a8" && (
              <IconButton
                size="small"
                sx={{
                  bgcolor: "#EF444415",
                  color: "#EF4444",
                  "&:hover": { bgcolor: "#EF4444", color: "white" },
                }}
                onClick={(event) => handleDelete(event, cellValues)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}

            <IconButton
              size="small"
              sx={{
                bgcolor: cellValues.row.isCustomer == "true" ? "#F59E0B15" : "#10B98115",
                color: cellValues.row.isCustomer == "true" ? "#F59E0B" : "#10B981",
                "&:hover": {
                  bgcolor: cellValues.row.isCustomer == "true" ? "#F59E0B" : "#10B981",
                  color: "white",
                },
              }}
              onClick={(event) => {
                if (cellValues.row.isCustomer == "true") {
                  setIsCustomerUpdating(true);
                  handleCustomerModal(event, cellValues);
                  setCustomerPhone(cellValues.row.phone);
                  setUpdateExistingCustomer({
                    lead_id: cellValues.row.lead_id,
                    phone: cellValues.row.phone,
                    purchasedBrand: JSON.parse(cellValues.row.purchasedBrand),
                    purchasedModel: JSON.parse(cellValues.row.purchasedModel),
                    insuranceRenewalDate: cellValues.row.insuranceRenewalDate,
                    insuranceStatus: cellValues.row.insuranceStatus,
                    taxStatus: cellValues.row.taxStatus,
                    taxRenewalDate: cellValues.row.taxRenewalDate,
                    rcTransferStatus: cellValues.row.rcTransferStatus,
                    purchasedDate: cellValues.row.purchasedDate,
                    fullPaymentRecievedDate: cellValues.row.fullPaymentRecievedDate,
                    customerProfile: cellValues.row.customerProfile,
                  });
                } else {
                  handleCustomerModal(event, cellValues);
                  setCustomerPhone(cellValues.row.phone);
                }
              }}
            >
              {cellValues.row.isCustomer == "true" ? (
                <EditAttributes fontSize="small" />
              ) : (
                <GroupAddIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        );
      },
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: "0.875rem" }}>
          {params.value}
        </Typography>
      ),
    },
    { field: "phone", headerName: "Contact Number", width: 140, sortable: false },
    {
      field: "leadType",
      headerName: "Status",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value || "Open"}
          size="small"
          variant="outlined"
          color={
            params.value === "Open" ? "primary" : params.value === "Closed" ? "error" : "warning"
          }
          sx={{ fontWeight: 700, borderRadius: "6px" }}
        />
      ),
    },
    { field: "city", headerName: "Location", sortable: false, width: 140 },
    { field: "salesExecutive", headerName: "Sales Executive", width: 170, sortable: false },
    {
      field: "vehicleBrands",
      headerName: "Interested Brands",
      sortable: false,
      width: 200,
      renderCell: (params) => {
        try {
          const valueFormatted = typeof params.value === "string" ? JSON.parse(params.value) : params.value || [];
          return (
            <Stack direction="row" spacing={0.5} sx={{ overflowX: "auto", py: 1 }}>
              {valueFormatted.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  size="small"
                  sx={{
                    backgroundColor: "#EFF6FF",
                    color: "#1D4ED8",
                    fontWeight: 600,
                    border: "1px solid #BFDBFE",
                    borderRadius: "6px",
                  }}
                />
              ))}
            </Stack>
          );
        } catch {
          return null;
        }
      },
    },
    {
      field: "vehicleModels",
      headerName: "Preferred Models",
      width: 220,
      sortable: false,
      cellClassName: "wrap-text",
      renderCell: (params) => {
        try {
          const valueFormatted = typeof params.value === "string" ? JSON.parse(params.value) : params.value || [];
          return (
            <Stack direction="row" spacing={0.5} sx={{ overflowX: "auto", py: 1 }}>
              {valueFormatted.map((v) => (
                <Chip
                  key={v}
                  label={v}
                  size="small"
                  sx={{
                    bgcolor: "#F0FDF4",
                    color: "#15803D",
                    fontWeight: 600,
                    border: "1px solid #BBF7D0",
                    borderRadius: "6px",
                  }}
                />
              ))}
            </Stack>
          );
        } catch {
          return null;
        }
      },
    },
    { field: "leadSource", headerName: "Lead Source", width: 140, sortable: false },
    {
      field: "tradeIn",
      headerName: "Trade In",
      sortable: false,
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value || "No"}
          size="small"
          sx={{
            fontWeight: 700,
            borderRadius: "6px",
            bgcolor: params.value === "Yes" ? "#FEF3C7" : "#F1F5F9",
            color: params.value === "Yes" ? "#92400E" : "#64748B",
            border: params.value === "Yes" ? "1px solid #FDE68A" : "1px solid #E2E8F0",
          }}
        />
      ),
    },
    {
      field: "tradeInCar",
      headerName: "Trade In Car",
      sortable: false,
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "officeVisit",
      headerName: "Office Visit",
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || "No"}
          size="small"
          sx={{
            fontWeight: 700,
            borderRadius: "6px",
            bgcolor: params.value === "Yes" ? "#DCFCE7" : "#F1F5F9",
            color: params.value === "Yes" ? "#166534" : "#64748B",
            border: params.value === "Yes" ? "1px solid #BBF7D0" : "1px solid #E2E8F0",
          }}
        />
      ),
    },
    { field: "activityLog", headerName: "Activity History & Logs", sortable: false, width: 320 },
    {
      field: "createdAt",
      headerName: "Date Created",
      width: 140,
      sortable: false,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
  ];

  // States
  const { clearAuthData } = React.useContext(AuthContext);
  const [open, setOpen] = React.useState(false);
  const [customerModalOpen, setcustomerModalOpen] = React.useState(false);
  const [purchasedDate, setPurchasedDate] = React.useState(null);
  const [insuranceRenewalDate, setInsuranceRenewalDate] = React.useState(null);
  const [taxRenewalDate, setTaxRenewalDate] = React.useState(null);
  const [fullPaymentRecievedDate, setFullPaymentRecievedDate] = React.useState(null);
  const [isCustomerUpdating, setIsCustomerUpdating] = React.useState(false);
  const [leadClosingReasons, setleadClosingReasons] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [findLead, setfindLead] = React.useState("");
  const [vehicleBrandName, setvehicleBrandName] = React.useState([]);
  const [vehicleModelName, setvehicleModelName] = React.useState([]);
  const [carModelNames, setcarModelNames] = React.useState([]);
  const [leads, setleads] = React.useState([]);
  const [isLoading, setisLoading] = React.useState(false);
  const [IsUpdating, setIsUpdating] = React.useState(false);
  const [TodayDueFollowUpCount, setTodayDueFollowUpCount] = React.useState(0);
  const [leadsAddedTodayCount, setLeadsAddedTodayCount] = React.useState(0);
  const [isLeadSaving, setIsLeadSaving] = React.useState(false);
  const [salesExecutiveName, setSalesExecutiveName] = React.useState("");
  const [salesExecutiveleadsReport, setSalesExecutiveLeadsReport] = React.useState("");
  const [salesExecutiveDueFollowUpleadsReport, setsalesExecutiveDueFollowUpleadsReport] =
    React.useState("");
  const [openAvanceLeadReportModal, setopenAvanceLeadReportModal] = React.useState(false);
  const [customerPhone, setCustomerPhone] = React.useState("");

  const [newLead, setNewLead] = React.useState({
    customerName: "",
    phone: "",
    gender: "Not Specified",
    email: "",
    salesExecutive: "",
    state: "",
    city: "",
    pincode: "",
    address: "",
    leadType: "Open",
    leadSource: "",
    vehicleBrands: [],
    vehicleModels: [],
    budget: "TBD",
    testDrive: "No",
    purchaseType: "Outright",
    tradeIn: "",
    tradeInCar: "",
    dueFollowUp: "No",
    officeVisit: "",
    carAvailable: "No",
    premiumCustomer: "No",
    activityLog: "",
    closingReason: "",
  });

  const [newCustomer, setNewCustomer] = React.useState({
    lead_id: "",
    purchasedBrand: [],
    purchasedModel: [],
    purchasedDate: "",
    insuranceRenewalDate: "",
    insuranceStatus: "",
    taxStatus: "",
    taxRenewalDate: "",
    rcTransferStatus: "",
    fullPaymentRecievedDate: "",
    isCustomer: "true",
    customerProfile: "",
  });

  const [updateExistingCustomer, setUpdateExistingCustomer] = React.useState({
    lead_id: "",
    purchasedBrand: [],
    purchasedModel: [],
    purchasedDate: "",
    insuranceRenewalDate: "",
    insuranceStatus: "",
    taxStatus: "",
    taxRenewalDate: "",
    rcTransferStatus: "",
    fullPaymentRecievedDate: "",
    isCustomer: "true",
    customerProfile: "",
  });

  // UUID Generator
  function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // Date Conversion Helper
  function getStrToDate(timestampString) {
    if (!timestampString) return null;
    const timestampMs = parseInt(timestampString);
    const dateTimeObject = DateTime.fromMillis(timestampMs);
    return dateTimeObject;
  }

  // Handlers
  const handleClose = () => {
    setOpen(false);
    setcustomerModalOpen(false);
    setvehicleModelName([]);
    setvehicleBrandName([]);
    setNewLead({
      customerName: "",
      phone: "",
      gender: "Not Specified",
      email: "",
      salesExecutive: "",
      state: "",
      city: "",
      pincode: "",
      address: "",
      leadType: "Open",
      leadSource: "",
      vehicleBrands: [],
      vehicleModels: [],
      budget: "TBD",
      testDrive: "No",
      purchaseType: "Outright",
      tradeIn: "",
      tradeInCar: "",
      dueFollowUp: "No",
      officeVisit: "",
      carAvailable: "No",
      premiumCustomer: "No",
      activityLog: "",
      closingReason: "",
    });
    setFullPaymentRecievedDate(null);
    setInsuranceRenewalDate(null);
    setPurchasedDate(null);
    setTaxRenewalDate(null);
    setIsUpdating(false);
    setNewCustomer({
      lead_id: "",
      purchasedBrand: [],
      purchasedModel: [],
      purchasedDate: "",
      insuranceRenewalDate: "",
      insuranceStatus: "",
      taxStatus: "",
      taxRenewalDate: "",
      rcTransferStatus: "",
      fullPaymentRecievedDate: "",
      isCustomer: "true",
      customerProfile: "",
    });
    setUpdateExistingCustomer({
      lead_id: "",
      purchasedBrand: [],
      purchasedModel: [],
      purchasedDate: "",
      insuranceRenewalDate: "",
      insuranceStatus: "",
      taxStatus: "",
      taxRenewalDate: "",
      rcTransferStatus: "",
      fullPaymentRecievedDate: "",
      isCustomer: "true",
      customerProfile: "",
    });
    setIsCustomerUpdating(false);
    setIsLeadSaving(false);
  };

  function handleChange(evt) {
    setNewLead({ ...newLead, [evt.target.name]: evt.target.value });
  }

  function handleFindLead(evnt) {
    setfindLead(evnt.target.value);
  }

  function findOneLead() {
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    axios
      .get(`/findLead/${findLead}`, config)
      .then((response) => {
        const salesExecutiveExists = response.data.data.salesExecutive;
        setfindLead("");
        if (salesExecutiveExists && salesExecutiveExists.length > 0) {
          toast.success(`This Lead Belongs to ${salesExecutiveExists}!`);
        }
      })
      .catch(() => {
        setfindLead("");
        toast.success(`New Lead!`);
      });
  }

  const handleBrandChange = (event, newValue) => {
    const updatedBrandName = typeof newValue === "string" ? newValue.split(",") : newValue;
    setvehicleBrandName(updatedBrandName);
    setNewLead((prev) => ({ ...prev, vehicleBrands: updatedBrandName }));
    if (isCustomerUpdating) {
      setUpdateExistingCustomer((prev) => ({ ...prev, purchasedBrand: updatedBrandName }));
    } else {
      setNewCustomer((prev) => ({ ...prev, purchasedBrand: updatedBrandName }));
    }
  };

  const handleModelChange = (event, newValue) => {
    const updatedModelName = typeof newValue === "string" ? newValue.split(",") : newValue;
    setvehicleModelName(updatedModelName);
    setNewLead((prev) => ({ ...prev, vehicleModels: updatedModelName }));
    if (isCustomerUpdating) {
      setUpdateExistingCustomer((prev) => ({ ...prev, purchasedModel: updatedModelName }));
    } else {
      setNewCustomer((prev) => ({ ...prev, purchasedModel: updatedModelName }));
    }
  };

  const handleEdit = (Event, Value) => {
    setOpen(true);
    setIsUpdating(true);
    setNewLead({
      lead_id: Value.row.lead_id,
      customerName: Value.row.customerName,
      phone: Value.row.phone,
      gender: Value.row.gender,
      email: Value.row.email,
      salesExecutive: Value.row.salesExecutive,
      state: Value.row.state,
      city: Value.row.city,
      pincode: Value.row.pincode,
      address: Value.row.address,
      leadType: Value.row.leadType,
      leadSource: Value.row.leadSource,
      vehicleBrands: JSON.parse(Value.row.vehicleBrands),
      vehicleModels: JSON.parse(Value.row.vehicleModels),
      budget: Value.row.budget,
      testDrive: Value.row.testDrive,
      purchaseType: Value.row.purchaseType,
      tradeIn: Value.row.tradeIn,
      tradeInCar: Value.row.tradeInCar,
      dueFollowUp: Value.row.dueFollowUp,
      officeVisit: Value.row.officeVisit,
      carAvailable: Value.row.carAvailable,
      premiumCustomer: Value.row.premiumCustomer,
      activityLog: Value.row.activityLog,
      closingReason: Value.row.closingReason,
    });
    setvehicleBrandName(JSON.parse(Value.row.vehicleBrands));
    setvehicleModelName(JSON.parse(Value.row.vehicleModels));
  };

  const handleDelete = async (Event, Value) => {
    if (window.confirm("You surely want to delete this Lead?")) {
      const token = localStorage.getItem("accessToken");
      const config = { headers: { auth: token } };
      await axios
        .delete(`/deleteLead/${Value.row.phone}`, config)
        .then(() => {
          toast.success("Lead Deleted Successfully!");
          getAllLeads();
        })
        .catch(() => {
          toast.error("Lead Cannot Be Deleted");
        });
    }
  };

  const handleNewCustomer = (evt) => {
    setNewCustomer({
      ...newCustomer,
      [evt.target.name]: evt.target.value,
      purchasedDate: purchasedDate ? JSON.stringify(purchasedDate.ts) : "",
      fullPaymentRecievedDate: fullPaymentRecievedDate
        ? JSON.stringify(fullPaymentRecievedDate.ts)
        : "",
      insuranceRenewalDate: insuranceRenewalDate ? JSON.stringify(insuranceRenewalDate.ts) : "",
      taxRenewalDate: taxRenewalDate ? JSON.stringify(taxRenewalDate.ts) : "",
      phone: customerPhone,
    });
  };

  const handleUpdateCustomer = async (evt) => {
    const value = evt.target.value;

    const getCustomDatePurchase = async () => {
      if (purchasedDate) {
        return JSON.stringify(purchasedDate.ts);
      } else if (isCustomerUpdating) {
        return updateExistingCustomer.purchasedDate;
      } else {
        return "";
      }
    };

    const getCustomDateInsurance = async () => {
      if (insuranceRenewalDate) {
        return JSON.stringify(insuranceRenewalDate.ts);
      } else if (isCustomerUpdating) {
        return updateExistingCustomer.insuranceRenewalDate;
      } else {
        return "";
      }
    };

    const getCustomDateTaxRenewal = async () => {
      if (taxRenewalDate) {
        return JSON.stringify(taxRenewalDate.ts);
      } else if (isCustomerUpdating) {
        return updateExistingCustomer.taxRenewalDate;
      } else {
        return "";
      }
    };

    const getCustomDateFullPayment = async () => {
      if (fullPaymentRecievedDate) {
        return JSON.stringify(fullPaymentRecievedDate.ts);
      } else if (isCustomerUpdating) {
        return updateExistingCustomer.fullPaymentRecievedDate;
      } else {
        return "";
      }
    };

    setUpdateExistingCustomer({
      ...updateExistingCustomer,
      [evt.target.name]: value,
      purchasedDate: await getCustomDatePurchase(),
      fullPaymentRecievedDate: await getCustomDateFullPayment(),
      insuranceRenewalDate: await getCustomDateInsurance(),
      taxRenewalDate: await getCustomDateTaxRenewal(),
      phone: customerPhone,
    });
  };

  // Add New Lead with Streamlined Validation & Safe Defaults
  const addNewLead = async () => {
    setIsLeadSaving(true);

    function isInvalid(value) {
      return value === undefined || value === null || value.length === 0;
    }

    if (isInvalid(newLead.phone)) {
      toast.warn("Phone Number Missing");
      setIsLeadSaving(false);
    } else if (newLead.phone.length < 10 && newLead.phone.length > 0) {
      toast.warn("Phone Less Than 10 Digits");
      setIsLeadSaving(false);
    } else if (newLead.phone.length > 10) {
      toast.warn("Phone cannot be greater than 10 Digits");
      setIsLeadSaving(false);
    } else if (newLead.phone.length === 10) {
      if (
        isInvalid(newLead.customerName) ||
        isInvalid(newLead.salesExecutive) ||
        isInvalid(newLead.city) ||
        isInvalid(newLead.leadSource) ||
        isInvalid(newLead.vehicleBrands) ||
        isInvalid(newLead.vehicleModels) ||
        isInvalid(newLead.tradeIn) ||
        (newLead.tradeIn === "Yes" && isInvalid(newLead.tradeInCar)) ||
        isInvalid(newLead.officeVisit) ||
        isInvalid(newLead.activityLog)
      ) {
        setIsLeadSaving(false);
        toast.error("Please fill all required fields!");
      } else {
        const token = localStorage.getItem("accessToken");
        const config = { headers: { auth: token } };
        const payload = {
          ...newLead,
          gender: newLead.gender || "Not Specified",
          budget: newLead.budget || "TBD",
          dueFollowUp: "No", // default false
          officeVisit: newLead.officeVisit || "No",
          leadType: newLead.leadType || "Open",
          lead_id: JSON.stringify(uuid()),
        };
        await axios
          .post("/addlead", payload, config)
          .then(() => {
            toast.success("Lead Added Successfully!");
            setIsLeadSaving(false);
            handleClose();
            getAllLeads();
            getTodayDueFollowUps();
            getTodayLeadsAdded();
          })
          .catch((err) => {
            if (err.response?.data?.error === "Lead Already Exists") {
              toast.error("Lead Already Exists");
            } else {
              toast.error("Lead Cannot Be Added");
            }
            setIsLeadSaving(false);
          });
      }
    } else {
      toast.error("Phone is mandatory");
      setIsLeadSaving(false);
    }
  };

  // Update Lead with Validation & Safe Defaults
  const updateLead = async () => {
    setIsLeadSaving(true);
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };

    function isInvalid(value) {
      return value === undefined || value === null || value.length === 0;
    }

    if (
      isInvalid(newLead.customerName) ||
      isInvalid(newLead.salesExecutive) ||
      isInvalid(newLead.city) ||
      isInvalid(newLead.leadSource) ||
      isInvalid(newLead.vehicleBrands) ||
      isInvalid(newLead.vehicleModels) ||
      isInvalid(newLead.tradeIn) ||
      (newLead.tradeIn === "Yes" && isInvalid(newLead.tradeInCar)) ||
      isInvalid(newLead.officeVisit) ||
      isInvalid(newLead.activityLog) ||
      (newLead.leadType === "Closed" && !newLead.closingReason)
    ) {
      toast.error("Please fill all required fields!");
      setIsLeadSaving(false);
    } else {
      const payload = {
        ...newLead,
        gender: newLead.gender || "Not Specified",
        budget: newLead.budget || "TBD",
        dueFollowUp: newLead.dueFollowUp || "No",
        officeVisit: newLead.officeVisit || "No",
        leadType: newLead.leadType || "Open",
      };
      await axios
        .put("/updateLead", payload, config)
        .then(() => {
          toast.success("Leads Updated Successfully!");
          setIsLeadSaving(false);
          handleClose();
          getAllLeads();
          getTodayDueFollowUps();
          getTodayLeadsAdded();
        })
        .catch((err) => {
          if (err.response?.data?.error === "Lead Already Exists") {
            toast.error("Lead Already Exists");
          } else {
            toast.error("Lead Cannot Be Updated");
          }
          setIsLeadSaving(false);
        });
    }
  };

  // Add/Update Customer Functions
  const addNewCustomer = async () => {
    setIsLeadSaving(true);
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };

    function isInvalid(value) {
      return value === undefined || value.length === 0;
    }

    if (
      isInvalid(newCustomer.purchasedBrand) ||
      isInvalid(newCustomer.purchasedModel) ||
      isInvalid(newCustomer.insuranceStatus) ||
      isInvalid(newCustomer.taxStatus) ||
      isInvalid(newCustomer.purchasedDate) ||
      isInvalid(newCustomer.insuranceRenewalDate) ||
      isInvalid(newCustomer.taxRenewalDate) ||
      isInvalid(newCustomer.fullPaymentRecievedDate) ||
      isInvalid(newCustomer.rcTransferStatus) ||
      isInvalid(newCustomer.customerProfile)
    ) {
      toast.error("Please fill all required fields!");
      setIsLeadSaving(false);
    } else {
      await axios
        .put(
          "/addCustomer",
          newCustomer.lead_id && newCustomer.lead_id.length > 0
            ? newCustomer
            : { ...newCustomer, lead_id: "", isCustomer: "true" },
          config
        )
        .then(() => {
          toast.success("Customer Added Successfully!");
          setIsLeadSaving(false);
          handleClose();
          getAllLeads();
        })
        .catch(() => {
          toast.error("Customer Cannot Be Added");
          setIsLeadSaving(false);
        });
    }
  };

  const UpdateCustomer = async () => {
    setIsLeadSaving(true);
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };

    function isInvalid(value) {
      return value === undefined || value.length === 0;
    }

    const dataToValidate = isCustomerUpdating ? updateExistingCustomer : newCustomer;

    if (
      isInvalid(dataToValidate.purchasedBrand) ||
      isInvalid(dataToValidate.purchasedModel) ||
      isInvalid(dataToValidate.insuranceStatus) ||
      isInvalid(dataToValidate.taxStatus) ||
      isInvalid(dataToValidate.purchasedDate) ||
      isInvalid(dataToValidate.insuranceRenewalDate) ||
      isInvalid(dataToValidate.taxRenewalDate) ||
      isInvalid(dataToValidate.fullPaymentRecievedDate) ||
      isInvalid(dataToValidate.rcTransferStatus) ||
      isInvalid(dataToValidate.customerProfile)
    ) {
      toast.error("Please fill all required fields!");
      setIsLeadSaving(false);
    } else {
      await axios
        .put("/updateCustomer", isCustomerUpdating ? updateExistingCustomer : newCustomer, config)
        .then(() => {
          toast.success("Customer Updated Successfully!");
          setIsLeadSaving(false);
          handleClose();
          getAllLeads();
        })
        .catch(() => {
          toast.error("Customer Cannot Be Updated");
          setIsLeadSaving(false);
        });
    }
  };

  // API Calls
  const getAllVehicleModels = async () => {
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    axios.get("/vehiclemodels", config).then((response) => setcarModelNames(response.data.data));
  };

  const getTodayDueFollowUps = async () => {
    const roleID = localStorage.getItem("roleId");
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    const salesExecutive = localStorage.getItem("fullName");

    if (roleID != "af7c1fe6-d669-414e-b066-e9733f0de7a8") {
      axios
        .get(`/todayDueFollowUpsSalesExecutive/${salesExecutive}`, config)
        .then((response) => setTodayDueFollowUpCount(response.data.todayDueFollowUp.count));
    } else {
      axios
        .get("/todayDueFollowUps", config)
        .then((response) => setTodayDueFollowUpCount(response.data.todayDueFollowUp.count));
    }
  };

  const getTodayLeadsAdded = async () => {
    const roleID = localStorage.getItem("roleId");
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    const salesExecutive = localStorage.getItem("fullName");

    if (roleID != "af7c1fe6-d669-414e-b066-e9733f0de7a8") {
      axios
        .get(`/leadsAddedTodaySalesExecutive/${salesExecutive}`, config)
        .then((response) => setLeadsAddedTodayCount(response.data.count.totalLeadCount));
    } else {
      axios
        .get("/leadsAddedToday", config)
        .then((response) => setLeadsAddedTodayCount(response.data.count.count));
    }
  };

  const getAllLeads = async (
    currentPage = page,
    currentPageSize = pageSize,
    search = searchQuery
  ) => {
    setisLoading(true);
    const roleID = localStorage.getItem("roleId");
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    try {
      if (roleID != "af7c1fe6-d669-414e-b066-e9733f0de7a8") {
        const salesExecutive = localStorage.getItem("fullName");
        const response = await axios.get(
          `/salesLeads/${salesExecutive}?page=${currentPage}&limit=${currentPageSize}&search=${encodeURIComponent(search)}`,
          config
        );
        setleads(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.totalCount);
      } else {
        const response = await axios.get(
          `/leads?page=${currentPage}&limit=${currentPageSize}&search=${encodeURIComponent(search)}`,
          config
        );
        setleads(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.totalCount);
      }
      setisLoading(false);
    } catch (error) {
      setisLoading(false);
    }
  };

  const getAllReasons = async () => {
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    axios.get("/closingReasons", config).then((response) => {
      setleadClosingReasons(response.data.data);
    });
  };

  // Pagination & Search Handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
    getAllLeads(newPage, pageSize, searchQuery);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
    getAllLeads(1, newPageSize, searchQuery);
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearchChange = React.useCallback(
    debounce((searchValue) => {
      setSearchQuery(searchValue);
      setPage(1);
      getAllLeads(1, pageSize, searchValue);
    }, 500),
    [pageSize]
  );

  const handleCustomerModal = () => setcustomerModalOpen(true);
  const handleAdvanceReportModalOpen = () => setopenAvanceLeadReportModal(true);
  const handleAdvanceReportModalClose = () => {
    setopenAvanceLeadReportModal(false);
    setSalesExecutiveName("");
    setSalesExecutiveLeadsReport("");
  };

  const getSalesExecutiveReportFunc = async () => {
    const token = localStorage.getItem("accessToken");
    const config = { headers: { auth: token } };
    const res1 = await axios.get(`/leadsAddedTodaySalesExecutive/${salesExecutiveName}`, config);
    setSalesExecutiveLeadsReport(res1.data.count.totalLeadCount);
    const res2 = await axios.get(`/todayDueFollowUpsSalesExecutive/${salesExecutiveName}`, config);
    setsalesExecutiveDueFollowUpleadsReport(res2.data.todayDueFollowUp.count);
  };

  React.useEffect(() => {
    getAllVehicleModels();
    getAllLeads();
    getTodayDueFollowUps();
    getTodayLeadsAdded();
    getAllReasons();
  }, []);

  return (
    <ContentArea>
      {/* HEADER SECTION */}
      <Stack 
        direction={{ xs: "column", md: "row" }} 
        justifyContent="space-between" 
        alignItems={{ xs: "flex-start", md: "center" }} 
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "#1E293B",
              fontFamily: "Kamerik",
              letterSpacing: "-1.5px",
              fontSize: { xs: "2rem", md: "3rem" }
            }}
          >
            Leads Community
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
            Management hub for all customer enquiries and follow-ups.
          </Typography>
        </Box>

        <Stack 
          direction={{ xs: "column", sm: "row" }} 
          spacing={2} 
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <SearchContainer>
            <SearchIcon sx={{ color: "#94A3B8", fontSize: "20px", mr: 1 }} />
            <InputBase
              placeholder="Quick Search Lead..."
              value={findLead}
              onChange={handleFindLead}
              sx={{ flexGrow: 1, fontSize: "14px" }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={findOneLead}
              sx={{ borderRadius: "8px", bgcolor: "#1E293B" }}
            >
              Find
            </Button>
          </SearchContainer>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ 
                borderRadius: "12px", 
                px: 3, 
                bgcolor: "#eb1d27", 
                "&:hover": { bgcolor: "#9e1c29" },
                height: { xs: "48px", sm: "auto" }
            }}
          >
            New Lead
          </Button>
        </Stack>
      </Stack>

      {/* STATS SECTION */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        sx={{ mb: 4, flexWrap: "wrap" }}
      >
        <StatChip color="#10B981">
          <PhoneCallback fontSize="small" />
          Today's FollowUps: {TodayDueFollowUpCount}
        </StatChip>
        <StatChip color="#6366F1">
          <AddchartIcon fontSize="small" />
          Leads Added Today: {leadsAddedTodayCount}
        </StatChip>

        {localStorage.getItem("roleId") == "af7c1fe6-d669-414e-b066-e9733f0de7a8" && (
          <Button
            variant="text"
            onClick={handleAdvanceReportModalOpen}
            sx={{ color: "#64748B", fontWeight: 700, justifyContent: { xs: "flex-start", sm: "center" } }}
          >
            View Advance Report
          </Button>
        )}
      </Stack>

      {/* DATA GRID SECTION */}
      <GlassCard elevation={0}>
        <Box sx={{ height: "70vh", width: "100%" }}>
          <StyledDataGrid
            rows={leads}
            columns={columns}
            getRowId={(row) => row.lead_id || row.phone}
            loading={isLoading}
            paginationMode="server"
            rowCount={totalCount}
            pageSize={pageSize}
            page={page - 1}
            onPageChange={(p) => handlePageChange(p + 1)}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            disableSelectionOnClick
            checkboxSelection
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            onFilterModelChange={(model) => {
              const searchValue = model.quickFilterValues?.[0] || "";
              handleSearchChange(searchValue);
            }}
            getRowClassName={(params) => (params.row.isCustomer == "true" ? "highlighted-row" : "")}
            sortModel={[{ field: "createdAt", sort: "desc" }]}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2,
            color: "#64748B",
            fontSize: "0.875rem",
          }}
        >
          <Typography variant="body2">
            Showing {leads.length > 0 ? (page - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(page * pageSize, totalCount)} of {totalCount} leads
          </Typography>
          <Typography variant="body2">
            Page {page} of {totalPages}
          </Typography>
        </Box>
      </GlassCard>

      {/* ADD/UPDATE LEAD MODAL */}
      <Modal
        open={open}
        onClose={handleClose}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
          },
        }}
      >
        <ModalBox>
          {/* HEADER BAR */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
                  border: "1px solid #BFDBFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.12)",
                }}
              >
                <PersonAddAlt1Icon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: "#0F172A",
                    letterSpacing: "-0.5px",
                    fontSize: { xs: "1.25rem", sm: "1.45rem" },
                  }}
                >
                  {IsUpdating ? "Update Lead Record" : "Register New Lead"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500, mt: 0.3 }}>
                  Onboard potential clients and capture automotive requirements for sales follow-up
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                border: "1px solid #E2E8F0",
                color: "#64748B",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#F8FAFC",
                  borderColor: "#CBD5E1",
                  color: "#0F172A",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Grid container spacing={3}>
            {/* LEFT COLUMN - Client Information Card */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: "18px",
                  p: { xs: 2.5, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  height: "100%",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "#EFF6FF",
                      color: "#2563EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonOutlineIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.82rem",
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      color: "#1E293B",
                    }}
                  >
                    Client Information
                  </Typography>
                </Stack>

                <TextField
                  fullWidth
                  label="Customer Name *"
                  name="customerName"
                  placeholder="e.g. John Doe"
                  value={newLead.customerName}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Contact Number *"
                  name="phone"
                  placeholder="10-digit mobile number"
                  value={newLead.phone}
                  onChange={handleChange}
                  disabled={IsUpdating}
                  inputProps={{ maxLength: 10 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                  }}
                />

                <FormControl
                  fullWidth
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                  }}
                >
                  <InputLabel>Sales Executive *</InputLabel>
                  <Select
                    name="salesExecutive"
                    value={newLead.salesExecutive}
                    onChange={handleChange}
                    label="Sales Executive *"
                  >
                    <MenuItem value="Riyaz Ahmed">Riyaz Ahmed</MenuItem>
                    <MenuItem value="Adil Kamal">Adil Kamal</MenuItem>
                    <MenuItem value="Syed Kamran Hassan">Syed Kamran Hassan</MenuItem>
                    <MenuItem value="Saif Ahmed">Saif Ahmed</MenuItem>
                    <MenuItem value="Uzair Nadeem">Uzair Nadeem</MenuItem>
                    <MenuItem value="Saad Ahmed">Saad Ahmed</MenuItem>
                    <MenuItem value="Jaspreetraj Singh">Jaspreetraj Singh</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Location *"
                  name="city"
                  placeholder="City / Region"
                  value={newLead.city}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PlaceIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Activity History & Logs *"
                  name="activityLog"
                  placeholder="Record initial client call notes, preferences, or timeline..."
                  value={newLead.activityLog}
                  onChange={handleChange}
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* RIGHT COLUMN - Vehicle Preferences & Details Card */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: "18px",
                  p: { xs: 2.5, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  height: "100%",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "#F0FDF4",
                      color: "#16A34A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DirectionsCarIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.82rem",
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      color: "#1E293B",
                    }}
                  >
                    Vehicle Preferences & Details
                  </Typography>
                </Stack>

                <Autocomplete
                  multiple
                  options={carBrandNames || []}
                  value={newLead.vehicleBrands || vehicleBrandName}
                  onChange={handleBrandChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Interested Brands *"
                      placeholder="Select brands"
                      sx={{
                        bgcolor: "#FFFFFF",
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#FFFFFF",
                          "&:hover fieldset": { borderColor: "#94A3B8" },
                          "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                        },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{
                          bgcolor: "#EFF6FF",
                          color: "#1D4ED8",
                          fontWeight: 700,
                          border: "1px solid #BFDBFE",
                          borderRadius: "8px",
                        }}
                      />
                    ))
                  }
                />

                <Autocomplete
                  multiple
                  options={
                    carModelNames
                      .filter((obj) =>
                        (newLead.vehicleBrands || vehicleBrandName).includes(obj.brandName)
                      )
                      .map((obj) => obj.ModelName) || []
                  }
                  value={newLead.vehicleModels || vehicleModelName}
                  onChange={handleModelChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Preferred Models *"
                      placeholder="Select models"
                      sx={{
                        bgcolor: "#FFFFFF",
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#FFFFFF",
                          "&:hover fieldset": { borderColor: "#94A3B8" },
                          "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                        },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{
                          bgcolor: "#F0FDF4",
                          color: "#15803D",
                          fontWeight: 700,
                          border: "1px solid #BBF7D0",
                          borderRadius: "8px",
                        }}
                      />
                    ))
                  }
                />

                <FormControl
                  fullWidth
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      "&:hover fieldset": { borderColor: "#94A3B8" },
                      "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                    },
                  }}
                >
                  <InputLabel>Lead Source *</InputLabel>
                  <Select
                    name="leadSource"
                    value={newLead.leadSource}
                    onChange={handleChange}
                    label="Lead Source *"
                  >
                    <MenuItem value="OLX">OLX</MenuItem>
                    <MenuItem value="Car Trade">Car Trade</MenuItem>
                    <MenuItem value="Direct Call">Direct Call</MenuItem>
                    <MenuItem value="Social Media">Social Media</MenuItem>
                    <MenuItem value="Referrals">Referrals</MenuItem>
                    <MenuItem value="Personal Relation">Personal Relation</MenuItem>
                    <MenuItem value="Facebook">Facebook</MenuItem>
                    <MenuItem value="Whatsapp">Whatsapp</MenuItem>
                    <MenuItem value="Instagram">Instagram</MenuItem>
                    <MenuItem value="Website">Website</MenuItem>
                    <MenuItem value="Events">Events</MenuItem>
                    <MenuItem value="Campaign">Campaign</MenuItem>
                    <MenuItem value="Showroom Visit">Showroom Visit</MenuItem>
                    <MenuItem value="Cars24">Cars24</MenuItem>
                  </Select>
                </FormControl>

                {/* TRADE IN & OFFICE VISIT TOGGLE CONTROLS */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        borderRadius: "12px",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#475569", fontWeight: 700, mb: 1, display: "block" }}
                      >
                        Trade In Existing Car? *
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {["Yes", "No"].map((option) => {
                          const isSelected = newLead.tradeIn === option;
                          return (
                            <Button
                              key={option}
                              fullWidth
                              size="small"
                              variant={isSelected ? "contained" : "outlined"}
                              onClick={() =>
                                setNewLead((prev) => ({
                                  ...prev,
                                  tradeIn: option,
                                  ...(option === "No" ? { tradeInCar: "" } : {}),
                                }))
                              }
                              sx={{
                                height: "36px",
                                borderRadius: "8px",
                                fontWeight: 700,
                                textTransform: "none",
                                bgcolor: isSelected ? "#1E293B" : "transparent",
                                color: isSelected ? "#FFFFFF" : "#64748B",
                                borderColor: isSelected ? "#1E293B" : "#E2E8F0",
                                "&:hover": {
                                  bgcolor: isSelected ? "#0F172A" : "#F8FAFC",
                                  borderColor: isSelected ? "#0F172A" : "#CBD5E1",
                                },
                              }}
                            >
                              {option}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        borderRadius: "12px",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#475569", fontWeight: 700, mb: 1, display: "block" }}
                      >
                        Office Visit *
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {["Yes", "No"].map((option) => {
                          const isSelected = newLead.officeVisit === option;
                          return (
                            <Button
                              key={option}
                              fullWidth
                              size="small"
                              variant={isSelected ? "contained" : "outlined"}
                              onClick={() =>
                                setNewLead((prev) => ({
                                  ...prev,
                                  officeVisit: option,
                                }))
                              }
                              sx={{
                                height: "36px",
                                borderRadius: "8px",
                                fontWeight: 700,
                                textTransform: "none",
                                bgcolor: isSelected ? "#2563EB" : "transparent",
                                color: isSelected ? "#FFFFFF" : "#64748B",
                                borderColor: isSelected ? "#2563EB" : "#E2E8F0",
                                "&:hover": {
                                  bgcolor: isSelected ? "#1D4ED8" : "#F8FAFC",
                                  borderColor: isSelected ? "#1D4ED8" : "#CBD5E1",
                                },
                              }}
                            >
                              {option}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>

                {newLead.tradeIn === "Yes" && (
                  <TextField
                    fullWidth
                    label="Trade In Car Details *"
                    name="tradeInCar"
                    placeholder="e.g. 2018 Honda City V MT"
                    value={newLead.tradeInCar}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DirectionsCarIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      bgcolor: "#FFFFFF",
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#FFFFFF",
                        "&:hover fieldset": { borderColor: "#94A3B8" },
                        "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                      },
                    }}
                  />
                )}

                {IsUpdating && newLead.leadType === "Closed" && (
                  <FormControl
                    fullWidth
                    sx={{
                      bgcolor: "#FFFFFF",
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#FFFFFF",
                        "&:hover fieldset": { borderColor: "#94A3B8" },
                        "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                      },
                    }}
                  >
                    <InputLabel>Closing Reason *</InputLabel>
                    <Select
                      name="closingReason"
                      value={newLead.closingReason}
                      onChange={handleChange}
                      label="Closing Reason *"
                    >
                      {leadClosingReasons.map((r) => (
                        <MenuItem key={r.reason} value={r.reason}>
                          {r.reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* FOOTER ACTION BAR */}
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              mt: 3.5,
              pt: 2.5,
              borderTop: "1px solid #F1F5F9",
            }}
          >
            <Button
              variant="text"
              onClick={handleClose}
              sx={{
                color: "#64748B",
                fontWeight: 700,
                px: 3,
                height: "48px",
                borderRadius: "12px",
                textTransform: "none",
                "&:hover": { bgcolor: "#F1F5F9", color: "#0F172A" },
              }}
            >
              Cancel & Close
            </Button>

            <Button
              variant="contained"
              onClick={IsUpdating ? updateLead : addNewLead}
              disabled={isLeadSaving}
              startIcon={!isLeadSaving && <CheckCircleIcon sx={{ fontSize: 20 }} />}
              sx={{
                minWidth: { xs: "100%", sm: "240px" },
                height: "50px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
                fontSize: "0.95rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 10px 20px -5px rgba(15, 23, 42, 0.35)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #0F172A 0%, #020617 100%)",
                  boxShadow: "0 12px 24px -5px rgba(15, 23, 42, 0.45)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {isLeadSaving ? (
                <CircularProgress size={24} color="inherit" />
              ) : IsUpdating ? (
                "Update Lead Record"
              ) : (
                "Save & Register Lead"
              )}
            </Button>
          </Stack>
        </ModalBox>
      </Modal>

      {/* CUSTOMER CONVERSION MODAL */}
      <Modal open={customerModalOpen} onClose={handleClose}>
        <CustomerModalBox>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: "#1E293B" }}>
            {isCustomerUpdating ? "Edit Customer Details" : "Convert to Customer"}
          </Typography>
          <Stack spacing={2.5}>
            <Autocomplete
              multiple
              options={carBrandNames || []}
              value={isCustomerUpdating ? updateExistingCustomer.purchasedBrand : vehicleBrandName}
              onChange={handleBrandChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Purchased Brand *"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                    sx={{ bgcolor: "#1a5ea3", color: "white" }}
                  />
                ))
              }
            />

            <Autocomplete
              multiple
              options={
                carModelNames
                  .filter((obj) => {
                    const brands = isCustomerUpdating
                      ? updateExistingCustomer.purchasedBrand
                      : newLead.vehicleBrands || vehicleBrandName;
                    return brands.includes(obj.brandName);
                  })
                  .map((obj) => obj.ModelName) || []
              }
              value={isCustomerUpdating ? updateExistingCustomer.purchasedModel : vehicleModelName}
              onChange={handleModelChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Purchased Model *"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                    sx={{ bgcolor: "#1a5ea3", color: "white" }}
                  />
                ))
              }
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Insurance Status *</InputLabel>
                <Select
                  name="insuranceStatus"
                  value={
                    isCustomerUpdating
                      ? updateExistingCustomer.insuranceStatus
                      : newCustomer.insuranceStatus
                  }
                  onChange={isCustomerUpdating ? handleUpdateCustomer : handleNewCustomer}
                  label="Insurance Status *"
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="Valid">Valid</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Road Tax Status *</InputLabel>
                <Select
                  name="taxStatus"
                  value={
                    isCustomerUpdating ? updateExistingCustomer.taxStatus : newCustomer.taxStatus
                  }
                  onChange={isCustomerUpdating ? handleUpdateCustomer : handleNewCustomer}
                  label="Road Tax Status *"
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="Valid">Valid</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Typography variant="subtitle2" sx={{ color: "#64748B", fontWeight: 600, pt: 1 }}>
              Important Dates
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B", mb: 0.5, display: "block" }}>
                  Purchased Date *
                </Typography>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <StyledDatePicker
                    value={
                      purchasedDate ||
                      getStrToDate(
                        isCustomerUpdating
                          ? updateExistingCustomer.purchasedDate
                          : newCustomer.purchasedDate
                      )
                    }
                    onChange={(newValue) => setPurchasedDate(newValue)}
                  />
                </LocalizationProvider>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B", mb: 0.5, display: "block" }}>
                  Insurance Renewal *
                </Typography>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <StyledDatePicker
                    value={
                      insuranceRenewalDate ||
                      getStrToDate(
                        isCustomerUpdating
                          ? updateExistingCustomer.insuranceRenewalDate
                          : newCustomer.insuranceRenewalDate
                      )
                    }
                    onChange={(newValue) => setInsuranceRenewalDate(newValue)}
                  />
                </LocalizationProvider>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B", mb: 0.5, display: "block" }}>
                  Tax Renewal Date *
                </Typography>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <StyledDatePicker
                    value={
                      taxRenewalDate ||
                      getStrToDate(
                        isCustomerUpdating
                          ? updateExistingCustomer.taxRenewalDate
                          : newCustomer.taxRenewalDate
                      )
                    }
                    onChange={(newValue) => setTaxRenewalDate(newValue)}
                  />
                </LocalizationProvider>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B", mb: 0.5, display: "block" }}>
                  Full Payment Received *
                </Typography>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <StyledDatePicker
                    value={
                      fullPaymentRecievedDate ||
                      getStrToDate(
                        isCustomerUpdating
                          ? updateExistingCustomer.fullPaymentRecievedDate
                          : newCustomer.fullPaymentRecievedDate
                      )
                    }
                    onChange={(newValue) => setFullPaymentRecievedDate(newValue)}
                  />
                </LocalizationProvider>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>RC Transfer Status *</InputLabel>
                <Select
                  name="rcTransferStatus"
                  value={
                    isCustomerUpdating
                      ? updateExistingCustomer.rcTransferStatus
                      : newCustomer.rcTransferStatus
                  }
                  onChange={isCustomerUpdating ? handleUpdateCustomer : handleNewCustomer}
                  label="RC Transfer Status *"
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="Transferred">Transferred</MenuItem>
                  <MenuItem value="On Process">On Process</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Customer Profile *</InputLabel>
                <Select
                  name="customerProfile"
                  value={
                    isCustomerUpdating
                      ? updateExistingCustomer.customerProfile
                      : newCustomer.customerProfile
                  }
                  onChange={isCustomerUpdating ? handleUpdateCustomer : handleNewCustomer}
                  label="Customer Profile *"
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="Premium">Premium</MenuItem>
                  <MenuItem value="Average">Average</MenuItem>
                  <MenuItem value="Bad">Bad</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={isCustomerUpdating ? UpdateCustomer : addNewCustomer}
              sx={{
                py: 1.8,
                borderRadius: "12px",
                bgcolor: "#10B981",
                fontSize: "1rem",
                fontWeight: 700,
                "&:hover": { bgcolor: "#059669" },
              }}
              disabled={isLeadSaving}
            >
              {isLeadSaving ? (
                <CircularProgress size={24} color="inherit" />
              ) : isCustomerUpdating ? (
                "Update Customer"
              ) : (
                "Convert to Customer"
              )}
            </Button>
          </Stack>
        </CustomerModalBox>
      </Modal>

      {/* ADVANCE REPORT MODAL */}
      <Modal open={openAvanceLeadReportModal} onClose={handleAdvanceReportModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "500px",
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            p: 4,
            outline: "none",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: "#1E293B" }}>
            Daily Performance Report
          </Typography>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2}>
              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: "#F0FDFA",
                  borderRadius: "12px",
                  border: "1px solid #CCFBF1",
                }}
              >
                <Typography variant="caption" sx={{ color: "#0D9488", fontWeight: 700 }}>
                  TOTAL FOLLOWUPS
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#1E293B" }}>
                  {TodayDueFollowUpCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: "#FEF2F2",
                  borderRadius: "12px",
                  border: "1px solid #FEE2E2",
                }}
              >
                <Typography variant="caption" sx={{ color: "#DC2626", fontWeight: 700 }}>
                  NEW LEADS
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#1E293B" }}>
                  {leadsAddedTodayCount}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1E293B" }}>
              Executive Drill-down
            </Typography>
            <Stack direction="row" spacing={1}>
              <Select
                fullWidth
                size="small"
                value={salesExecutiveName}
                onChange={(e) => setSalesExecutiveName(e.target.value)}
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="Riyaz Ahmed">Riyaz Ahmed</MenuItem>
                <MenuItem value="Adil Kamal">Adil Kamal</MenuItem>
                <MenuItem value="Biswadeep Roy">Biswadeep Roy</MenuItem>
                <MenuItem value="Syed Kamran Hassan">Syed Kamran Hassan</MenuItem>
                <MenuItem value="Saad Ahmed">Saad Ahmed</MenuItem>
                <MenuItem value="Saif Ahmed">Saif Ahmed</MenuItem>
                <MenuItem value="Subham Das">Subham Das</MenuItem>
                <MenuItem value="Jaspreetraj Singh">Jaspreetraj Singh</MenuItem>
              </Select>
              <Button
                variant="contained"
                onClick={getSalesExecutiveReportFunc}
                sx={{ borderRadius: "10px", px: 3 }}
              >
                Check
              </Button>
            </Stack>

            {salesExecutiveleadsReport && (
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: "#F8FAFC",
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography variant="body2" sx={{ color: "#1E293B", lineHeight: 1.6 }}>
                  <strong>{salesExecutiveName}</strong> added{" "}
                  <strong>{salesExecutiveleadsReport}</strong> leads today with{" "}
                  <strong>{salesExecutiveDueFollowUpleadsReport}</strong> followups pending.
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Modal>

      <ToastContainer position="bottom-right" />
    </ContentArea>
  );
};

// Simple IconButton helper for cleaner actions
const IconButton = ({ children, onClick, sx, size }) => (
  <Button
    onClick={onClick}
    sx={{
      minWidth: "auto",
      p: 1,
      borderRadius: "8px",
      ...sx,
    }}
  >
    {children}
  </Button>
);

export default Leads;